/**
 * Resource Loading Fallback
 * Handles bundle loading failures with graceful degradation
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { Button } from './button';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

// Create a functional wrapper to use hooks
function ResourceErrorUI({ 
  isChunkError, 
  resourceName, 
  retryCount, 
  maxRetries, 
  isRetrying, 
  onRetry, 
  onReload 
}: {
  isChunkError: boolean;
  resourceName?: string;
  retryCount: number;
  maxRetries: number;
  isRetrying: boolean;
  onRetry: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation('common');
  const canRetry = retryCount < maxRetries;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4">
        <Alert variant="destructive">
          <AlertTitle>
            {isChunkError ? t('chunk_error_title') : t('resource_loading_error')}
          </AlertTitle>
          <AlertDescription>
            {isChunkError 
              ? t('chunk_error_message')
              : t('resource_loading_retry')
            }
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          {canRetry && (
            <Button 
              onClick={onRetry}
              disabled={isRetrying}
              className="w-full"
            >
              {isRetrying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('error_try_again')} ({maxRetries - retryCount} left)
            </Button>
          )}
          
          <Button 
            onClick={onReload}
            variant={canRetry ? "outline" : "default"}
            className="w-full"
          >
            {t('error_reload_app')}
          </Button>
        </div>

        {retryCount > 0 && (
          <p className="text-sm text-muted-foreground text-center">
            Retry attempt {retryCount}/{maxRetries}
          </p>
        )}
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
  resourceName?: string;
}

interface State {
  hasError: boolean;
  isRetrying: boolean;
  retryCount: number;
  error: Error | null;
}

export class ResourceLoadingFallback extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isRetrying: false,
      retryCount: 0,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 Resource loading failed:', error, errorInfo);
  }

  private isChunkLoadError = (error: Error): boolean => {
    return error.message.includes('chunk') ||
           error.message.includes('Loading') ||
           error.message.includes('import') ||
           error.name === 'ChunkLoadError';
  };

  private handleRetry = async () => {
    if (this.state.retryCount >= this.maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    // Clear module cache for chunk errors
    if (this.state.error && this.isChunkLoadError(this.state.error)) {
      // Clear dynamic import cache
      if ('webpackChunkName' in window) {
        delete (window as any).webpackChunkName;
      }
    }

    // Wait a bit before retrying
    await new Promise(resolve => setTimeout(resolve, 1000));

    this.setState(prevState => ({
      hasError: false,
      isRetrying: false,
      retryCount: prevState.retryCount + 1,
      error: null
    }));
  };

  private handleReload = async () => {
    try {
      // Use soft-first reload approach
      const { useNavigationHelper } = await import('@/hooks/useNavigationHelper');
      const { safeReload } = useNavigationHelper();
      
      // Clear caches first
      if (typeof window !== 'undefined' && 'caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }
      
      await safeReload('resource-loading-fallback');
    } catch (error) {
      try {
        window.history.replaceState(null, '', window.location.pathname);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (navError) {
        console.warn('Navigation recovery failed; please refresh manually', navError);
      }
    }
  };

  render() {
    if (this.state.hasError) {
      const isChunkError = this.state.error && this.isChunkLoadError(this.state.error);

      return (
        <ResourceErrorUI
          isChunkError={isChunkError}
          resourceName={this.props.resourceName}
          retryCount={this.state.retryCount}
          maxRetries={this.maxRetries}
          isRetrying={this.state.isRetrying}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}